import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { Trash2, RotateCcw, AlertOctagon } from "lucide-react";
import { getUser } from "../../lib/auth";

const ENTITY_LABEL = {
  reservations: "Reservas",
  contracts: "Contratos",
  payments: "Pagamentos",
  guests: "Hóspedes",
  houses: "Imóveis",
  checklists: "Checklists",
  blockings: "Bloqueios",
};

export default function AdminTrash() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("reservations");
  const user = getUser();
  const isDiretor = user?.role === "diretor";

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/admin/trash");
      setGroups(r.data.groups || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar Lixeira");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function restore(entity, id) {
    if (!confirm("Restaurar este registro da Lixeira?")) return;
    try {
      await api.post(`/admin/trash/${entity}/${id}/restore`);
      toast.success("Restaurado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao restaurar");
    }
  }
  async function hardDelete(entity, id) {
    if (!isDiretor) { toast.error("Apenas Diretor pode excluir definitivamente"); return; }
    const first = confirm("EXCLUSÃO DEFINITIVA. Este ato é irreversível. Confirma?");
    if (!first) return;
    const typed = prompt("Digite EXCLUIR_DEFINITIVAMENTE para confirmar:");
    if (typed !== "EXCLUIR_DEFINITIVAMENTE") { toast.error("Confirmação não bateu"); return; }
    try {
      await api.delete(`/admin/trash/${entity}/${id}?confirm=EXCLUIR_DEFINITIVAMENTE`);
      toast.success("Excluído definitivamente");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao excluir");
    }
  }

  const activeGroup = groups.find((g) => g.entity === active);

  return (
    <div className="max-w-6xl" data-testid="admin-trash-page">
      <div className="mb-8">
        <span className="text-[11px] uppercase tracking-[0.28em] text-slate-500">Administração</span>
        <h1 className="text-3xl font-semibold text-[#0AA89C] mt-1">Lixeira</h1>
        <p className="text-sm text-slate-500 mt-2 max-w-2xl">
          Registros excluídos ficam aqui por 30 dias antes da exclusão automática. Contratos assinados, pagamentos aprovados e reservas com histórico financeiro são preservados indefinidamente. Somente Diretor pode excluir definitivamente.
        </p>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Carregando…</div>
      ) : (
        <>
          <div className="flex gap-2 flex-wrap mb-4">
            {groups.map((g) => (
              <button
                key={g.entity}
                onClick={() => setActive(g.entity)}
                className={`text-xs px-3 py-1.5 rounded border ${active === g.entity ? "bg-[#0AA89C] text-white border-[#0AA89C]" : "bg-white text-slate-700 border-slate-300 hover:border-[#0AA89C]"}`}
                data-testid={`trash-tab-${g.entity}`}
              >
                {ENTITY_LABEL[g.entity]} <span className="ml-1 opacity-70">({g.count})</span>
              </button>
            ))}
          </div>

          {!activeGroup || activeGroup.count === 0 ? (
            <div className="border border-dashed border-slate-300 rounded-md p-12 text-center">
              <Trash2 className="w-8 h-8 text-slate-400 mx-auto" />
              <p className="text-sm text-slate-500 mt-3">Nenhum item de {ENTITY_LABEL[active]?.toLowerCase()} na Lixeira.</p>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-4 py-3 text-left">Identificador</th>
                    <th className="px-4 py-3 text-left">Excluído em</th>
                    <th className="px-4 py-3 text-left">Por</th>
                    <th className="px-4 py-3 text-left">Motivo</th>
                    <th className="px-4 py-3 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {activeGroup.items.map((it) => {
                    const label = it.code || it.name || it.guest_name || it.slug || it.id.slice(0, 8);
                    return (
                      <tr key={it.id} className="border-t border-slate-100" data-testid={`trash-row-${active}-${it.id}`}>
                        <td className="px-4 py-3 font-medium text-[#22302E]">
                          {label}
                          {it.status && <div className="text-xs text-slate-400">status: {it.status}</div>}
                        </td>
                        <td className="px-4 py-3 text-slate-700">{it.deleted_at?.slice(0, 19).replace("T", " ") || "—"}</td>
                        <td className="px-4 py-3 text-slate-700">{it.deleted_by || "—"}</td>
                        <td className="px-4 py-3 text-slate-600">{it.deleted_reason || "—"}</td>
                        <td className="px-4 py-3 text-right space-x-1">
                          <Button size="sm" variant="outline" onClick={() => restore(active, it.id)} data-testid={`trash-restore-${it.id}`}>
                            <RotateCcw className="w-3 h-3 mr-1" /> Restaurar
                          </Button>
                          {isDiretor && (
                            <Button size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" onClick={() => hardDelete(active, it.id)} data-testid={`trash-hard-delete-${it.id}`}>
                              <AlertOctagon className="w-3 h-3 mr-1" /> Excluir definitivamente
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
